<template>
    <div class="confirm-action-modal-wrapper">
        <div v-if="showComponentSection" class="confirm-action-modal-wrapper__components-wrapper">
            <component :is="component" />
        </div>

        <p v-if="showContent" class="mt-6 mb-16 text-center">
            {{ content }}
        </p>

        <div
            v-if="showHtmlContent"
            class="confirm-action-modal-wrapper__html-content"
            v-html="modalStore.data.htmlContent"
        ></div>

        <section v-if="isActAsOemActive()" class="mb-6">
            <p>
                {{
                    $t('§ui.warning_perform_action_as_oem', { oem: userStore.userData.owner_email })
                }}
            </p>
        </section>

        <CoreCheckbox
            v-if="checkbox"
            v-model="checkboxValue"
            class="mb-6"
            :name="checkbox.name"
            :label="checkbox.label"
            :disabled="isConfirmingCheckbox"
        />

        <Message v-if="isConfirmingCheckbox" class="mb-6" severity="warn" :closable="false">
            {{ $t(checkbox.confirmationText) }}
        </Message>

        <div class="flex gap-2">
            <CoreButton
                v-if="buttonLabel"
                ref="confirm-action-button"
                @click="handleExecuteAction"
                :disabled="executing"
                :loading="executing"
                :label="confirmButtonLabel"
                :variant="buttonVariant"
                :icon="buttonIcon"
            />

            <CoreButton
                icon="pi-times"
                variant="tertiary"
                :disabled="executing"
                :label="cancelButtonLabel"
                @click="handleClose"
            />
        </div>
    </div>
</template>

<script>
import { useModalStore } from '@/stores/modal'
import CoreButton from '@/components/core/CoreButton.vue'
import CoreCheckbox from '@/components/core/CoreCheckbox.vue'
import Message from 'primevue/message'
import { storeRegistry } from '@/stores/storeRegistry'
import { useUserStore } from '@/stores/user'

const TYPE_CONFIRM = 'confirm'
const TYPE_DELETE = 'delete'
const TYPE_DOWNLOAD = 'download'
const TYPE_ACCEPT = 'accept'

export default {
    name: 'ConfirmActionModal',

    components: {
        CoreButton,
        CoreCheckbox,
        Message,
    },

    data() {
        return {
            modalStore: useModalStore(),

            type: TYPE_CONFIRM,

            heading: '',

            content: '',

            action: null,

            buttonLabel: '',

            component: null,

            propsToPass: {},

            checkbox: null,

            checkboxValue: false,

            isConfirmingCheckbox: false,

            icon: 'pi-save',

            userStore: useUserStore(),

            executing: false,
        }
    },

    computed: {
        buttonVariant() {
            if (this.type === TYPE_DELETE) return 'danger'

            return 'primary'
        },

        buttonIcon() {
            if (this.isConfirmingCheckbox) return 'pi-check'
            if (this.type === TYPE_DELETE) return 'pi-trash'
            if (this.type === TYPE_CONFIRM) return 'pi-save'
            if (this.type === TYPE_DOWNLOAD) return 'pi-download'
            if (this.type === TYPE_ACCEPT) return 'pi-check'

            return 'pi-save'
        },

        translatedHeading() {
            return this.heading === ''
                ? this.$t('§ui.please_confirm_before_proceeding')
                : this.$t(this.heading)
        },

        translatedButtonLabel() {
            return this.buttonLabel === ''
                ? this.$t('§ui_button.confirm')
                : this.$t(this.buttonLabel)
        },

        confirmButtonLabel() {
            return this.isConfirmingCheckbox ? '§ui_button.confirm' : this.buttonLabel
        },

        checkboxNeedsConfirmation() {
            return (
                Boolean(this.checkbox?.confirmationText) &&
                this.checkboxValue !== (this.checkbox.value ?? false)
            )
        },

        showContent() {
            return this.content && this.content.length > 0
        },

        showComponentSection() {
            return this.component !== null
        },

        showHtmlContent() {
            return this.modalStore.data.htmlContent !== undefined
        },

        cancelButtonLabel() {
            return this.isActAsOemActive()
                ? this.$t('§ui_button.cancel_change_oem')
                : this.$t('§ui_button.cancel')
        },
    },

    mounted() {
        this.handleData({ ...this.modalStore.data })
        if (this.$refs['confirm-action-button']) {
            this.$refs['confirm-action-button'].$el.focus()
        }
    },

    methods: {
        handleData({ type, heading, content, buttonLabel, action, component, propsToPass, checkbox }) {
            this.type = type
            this.heading = heading
            this.content = content
            this.buttonLabel = buttonLabel
            this.action = action
            this.component = component
            this.propsToPass = propsToPass
            this.checkbox = checkbox || null
            this.checkboxValue = checkbox?.value ?? false
        },

        handleClose() {
            if (this.modalStore.data.onCancel) {
                this.modalStore.data.onCancel()
            }

            this.modalStore.setClose()
        },

        async handleExecuteAction(event) {
            if (this.executing) return

            if (this.checkboxNeedsConfirmation && !this.isConfirmingCheckbox) {
                this.isConfirmingCheckbox = true
                return
            }

            if (this.isConfirmingCheckbox && event?.detail > 1) return

            this.executing = true
            try {
                if (this.modalStore.data.onConfirm) {
                    this.modalStore.data.onConfirm()
                    this.modalStore.setClose()
                    return
                }

                const actionArgs = this.checkbox
                    ? { ...this.propsToPass, [this.checkbox.name]: this.checkboxValue }
                    : this.propsToPass

                if (typeof this.action === 'function') {
                    await this.action(actionArgs)
                } else if (typeof this.action === 'string') {
                    const [storeName, methodName] = this.action.split('/')

                    if (!storeRegistry[storeName]) {
                        console.error(`Store ${storeName} not found`)
                        return
                    }

                    const store = storeRegistry[storeName]()

                    if (typeof store[methodName] === 'function') {
                        await store[methodName](actionArgs)
                    } else {
                        console.error(`Method ${methodName} not found in store ${storeName}`)
                    }
                }

                this.handleClose()
            } catch (error) {
                console.error('Error executing action:', error)
            } finally {
                this.executing = false
            }
        },
    },
}
</script>

<style scoped>
.confirm-action-modal-wrapper__html-content {
    margin-bottom: 1.5rem;
    padding: 1rem;
    background-color: var(--color-neutral-300);
    border-radius: 4px;
    border: 1px solid var(--color-neutral-400);
}

.confirm-action-modal-wrapper__html-content p {
    margin: 0.5rem 0;
}

.confirm-action-modal-wrapper__html-content b {
    font-weight: 600;
}
</style>
